import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import BackButton from '@/components/ui/BackButton/BackButton.tsx';
import useRestaurantAdd from '@/api/useRestaurantAdd.ts';
import { MuiTelInput } from 'mui-tel-input';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { dayjsToTimeString, timeStringToDayjs } from '@/utils/dayjsToTimeString.ts';
import { AvailableTimezones } from '@/_constants/AvailableTimezones.ts';

export const schema = z.object({
  name: z
    .string()
    .min(1, { message: 'This field is required.' })
    .max(50, { message: 'Must be less than 100 characters.' }),
  address: z
    .string()
    .min(1, { message: 'This field is required.' })
    .max(50, { message: 'Must be less than 150 characters.' }),
  phone: z
    .string()
    .min(1, { message: 'This field is required.' })
    .transform((arg, ctx) => {
      const phone = parsePhoneNumberFromString(arg, {
        extract: false,
      });

      if (phone && phone.isValid()) {
        return phone.number;
      }

      // when it's not
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid phone number',
      });
      return z.NEVER;
    }),
  redirect_phone: z
    .string()
    .nullish()
    .transform((arg, ctx) => {
      if (!arg) {
        return z.NEVER;
      }

      const phone = parsePhoneNumberFromString(arg, {
        extract: false,
      });

      if (phone && phone.isValid()) {
        return phone.number;
      }

      // when it's not
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid phone number',
      });
      return z.NEVER;
    }),
  timezone: z.string().min(1, { message: 'This field is required.' }),
  weekday_hours_from: z.string().nullable(),
  weekday_hours_to: z.string().nullable(),
  weekend_hours_from: z.string().nullable(),
  weekend_hours_to: z.string().nullable(),
  bot_enabled: z.boolean(),
});

export type Schema = z.infer<typeof schema>;

type AddRestaurantModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
};
function AddRestaurantModal({ isOpen, onClose, onConfirm }: AddRestaurantModalProps) {
  const { t } = useTranslation();
  const {
    mutateAsync: addRestaurant,
    isError: isErrorAddRestaurant,
    isPending: isLoadingAddRestaurant,
    isSuccess: isSuccessAddRestaurant,
    error: errorAddRestaurant,
  } = useRestaurantAdd();

  const { handleSubmit, control, watch } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      address: '',
      phone: '',
      redirect_phone: undefined,
      timezone: 'UTC',
      weekday_hours_from: null,
      weekday_hours_to: null,
      weekend_hours_from: null,
      weekend_hours_to: null,
      bot_enabled: false,
    },
  });

  const handleAddRestaurant = (data: Schema) => {
    void (async () => {
      try {
        if (isLoadingAddRestaurant) {
          return;
        }

        await addRestaurant(data);

        if (onConfirm) {
          onConfirm();
        }

        onClose();
      } catch (error) {
        console.error('Error adding restaurant: ', error);
      }
    })();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <form className="modal-form" onSubmit={handleSubmit(handleAddRestaurant)}>
        <DialogTitle>{t('modals.addRestaurantModal.title')}</DialogTitle>
        <DialogContent>
          <div className="pt-2 flex flex-col gap-4">
            <div className="flex flex-col gap-2 empty:hidden">
              {isSuccessAddRestaurant && (
                <Alert severity="success">{t('modals.addRestaurantModal.successMessage')}</Alert>
              )}
              {isErrorAddRestaurant && <Alert severity="error">{errorAddRestaurant?.message as string}</Alert>}
            </div>
            <div className="flex flex-col gap-4 w-full">
              <Controller
                control={control}
                name="bot_enabled"
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    control={<Switch value={value} checked={value} onChange={onChange} />}
                    label={t('label.bot_enabled')}
                  />
                )}
              />
              <Controller
                control={control}
                name="name"
                render={({ field: { onChange, value }, formState: { errors } }) => (
                  <TextField
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    value={value}
                    onChange={onChange}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    variant="outlined"
                    label={t('label.name')}
                    placeholder={t('placeholder.name')}
                  />
                )}
              />
              <Controller
                control={control}
                name="address"
                render={({ field: { onChange, value }, formState: { errors } }) => (
                  <TextField
                    error={!!errors.address}
                    helperText={errors.address?.message}
                    value={value}
                    onChange={onChange}
                    multiline
                    minRows={2}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    variant="outlined"
                    label={t('label.address')}
                    placeholder={t('placeholder.address')}
                  />
                )}
              />
              <Controller
                control={control}
                name="phone"
                render={({ field: { onChange, value }, formState: { errors } }) => (
                  <MuiTelInput
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    value={value}
                    onChange={onChange}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    variant="outlined"
                    label={t('label.restaurant_phone')}
                    placeholder={t('placeholder.restaurant_phone')}
                  />
                )}
              />
              <Controller
                control={control}
                name="redirect_phone"
                render={({ field: { onChange, value }, formState: { errors } }) => (
                  <MuiTelInput
                    error={!!errors.redirect_phone}
                    helperText={errors.redirect_phone?.message}
                    value={value}
                    onChange={onChange}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    variant="outlined"
                    label={t('label.redirect_phone')}
                    placeholder={t('placeholder.redirect_phone')}
                  />
                )}
              />
              <Controller
                control={control}
                name="timezone"
                render={({ field: { onChange, value }, formState: { errors } }) => (
                  <FormControl error={!!errors.timezone}>
                    <InputLabel shrink id="timezone">
                      {t('label.timezone')}
                    </InputLabel>
                    <Select
                      value={value}
                      onChange={(event) => onChange(`${event.target.value}`)}
                      displayEmpty
                      variant="outlined"
                      label={t('label.timezone')}
                      labelId="timezone"
                      renderValue={(currentValue) =>
                        currentValue == '' ? (
                          <span className="text-[#a1a2a1]">{t('placeholder.timezone')}</span>
                        ) : (
                          AvailableTimezones?.find((timezone) => timezone === currentValue)
                        )
                      }
                    >
                      {AvailableTimezones?.map((timezone) => (
                        <MenuItem key={`timezone_${timezone}`} value={timezone}>
                          {timezone}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.timezone?.message}</FormHelperText>
                  </FormControl>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <Controller
                  control={control}
                  name="weekday_hours_from"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TimePicker
                      ampm={false}
                      className="col-span-2 lg:col-span-1"
                      views={['hours', 'minutes']}
                      value={value ? timeStringToDayjs(value) : null}
                      onChange={(newValue) => {
                        onChange(newValue ? dayjsToTimeString(newValue) : null);
                      }}
                      slotProps={{
                        textField: {
                          error: !!errors.weekday_hours_from,
                          helperText: errors.weekday_hours_from?.message,
                          variant: 'outlined',
                          placeholder: t('placeholder.weekday_hours_from'),
                          label: t('label.weekday_hours_from'),
                          InputLabelProps: {
                            shrink: true,
                          },
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="weekday_hours_to"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TimePicker
                      ampm={false}
                      className="col-span-2 lg:col-span-1"
                      views={['hours', 'minutes']}
                      value={value ? timeStringToDayjs(value) : null}
                      onChange={(newValue) => {
                        onChange(newValue ? dayjsToTimeString(newValue) : null);
                      }}
                      slotProps={{
                        textField: {
                          error: !!errors.weekday_hours_to,
                          helperText: errors.weekday_hours_to?.message,
                          variant: 'outlined',
                          placeholder: t('placeholder.weekday_hours_to'),
                          label: t('label.weekday_hours_to'),
                          InputLabelProps: {
                            shrink: true,
                          },
                        },
                      }}
                    />
                  )}
                />
                {watch('weekend_hours_from')}
                <Controller
                  control={control}
                  name="weekend_hours_from"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TimePicker
                      ampm={false}
                      className="col-span-2 lg:col-span-1"
                      views={['hours', 'minutes']}
                      value={value ? timeStringToDayjs(value) : null}
                      onChange={(newValue) => {
                        onChange(newValue ? dayjsToTimeString(newValue) : null);
                      }}
                      slotProps={{
                        textField: {
                          error: !!errors.weekend_hours_from,
                          helperText: errors.weekend_hours_from?.message,
                          variant: 'outlined',
                          placeholder: t('placeholder.weekend_hours_from'),
                          label: t('label.weekend_hours_from'),
                          InputLabelProps: {
                            shrink: true,
                          },
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="weekend_hours_to"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TimePicker
                      ampm={false}
                      className="col-span-2 lg:col-span-1"
                      views={['hours', 'minutes']}
                      value={value ? timeStringToDayjs(value) : null}
                      onChange={(newValue) => {
                        onChange(newValue ? dayjsToTimeString(newValue) : null);
                      }}
                      slotProps={{
                        textField: {
                          error: !!errors.weekend_hours_to,
                          helperText: errors.weekend_hours_to?.message,
                          variant: 'outlined',
                          placeholder: t('placeholder.weekend_hours_to'),
                          label: t('label.weekend_hours_to'),
                          InputLabelProps: {
                            shrink: true,
                          },
                        },
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <div className="flex flex-wrap grow p-4 pt-0 justify-between">
            <BackButton onClick={onClose} autoFocus />
            <LoadingButton color="primary" variant="contained" loading={isLoadingAddRestaurant} type="submit">
              {t('button.save')}
            </LoadingButton>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AddRestaurantModal;
